// ═══════════════════════════════════════════════════════════════════════════
// 1-Р АНГИЙН ЭЛСЭЛТИЙН ӨРГӨДӨЛ (Бага сургууль, 2026-2027)
// Ахлах/дунд ангийн үндсэн өргөдлөөс БҮРЭН ТУСДАА урсгал — эцэг эх бөглөнө,
// хураамжгүй. Үндсэн талбарууд тогтмол, асуумжийн асуултууд API-аас динамик.
// ═══════════════════════════════════════════════════════════════════════════
import { useEffect, useState } from "react";
import { instance } from "utils/axios";
import logo from "assets/images/logo.jpg";

const C = { purple: "#7B2FBE", yellow: "#FFD600", bg: "#f7f4fc", border: "#e8e0f5" };

const EMAIL_RE = /^\S+@\S+\.\S+$/;
const PHONE_RE = /^\d{8}$/;

// Регистрээс төрсөн огноо, хүйс задлах (GeneralInfo-той ижил логик)
function parseRD(reg) {
  if (!reg) return null;
  const chars = [...reg.trim()];
  const letters = chars.filter((c) => !/\d/.test(c));
  const digits = chars.filter((c) => /\d/.test(c)).join("");
  if (letters.length !== 2 || digits.length !== 8) return null;
  const yy = parseInt(digits.slice(0, 2), 10);
  const mmRaw = parseInt(digits.slice(2, 4), 10);
  const dd = parseInt(digits.slice(4, 6), 10);
  const gDigit = parseInt(digits[6], 10);
  if ([yy, mmRaw, dd, gDigit].some(isNaN) || dd < 1 || dd > 31) return null;
  let year = 2000 + yy;
  if (year > new Date().getFullYear()) year -= 100;
  let month;
  if (year < 2000) {
    if (mmRaw >= 1 && mmRaw <= 12) month = mmRaw;
    else return null;
  } else {
    if (mmRaw >= 21 && mmRaw <= 32) month = mmRaw - 20;
    else return null;
  }
  const gender = gDigit % 2 !== 0 ? "Эрэгтэй" : "Эмэгтэй";
  const birthDate = `${year}-${String(month).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
  return { birthDate, gender };
}

const inputStyle = (err) => ({
  width: "100%",
  padding: "11px 14px",
  borderRadius: 10,
  border: `1.5px solid ${err ? "#e53935" : C.border}`,
  fontSize: 15,
  outline: "none",
  fontFamily: "inherit",
  boxSizing: "border-box",
  background: "#fafafa",
});

function Label({ children, required }) {
  return (
    <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#444", marginBottom: 7 }}>
      {children}
      {required && <span style={{ color: "#e53935", marginLeft: 3 }}>*</span>}
    </label>
  );
}

function Card({ title, children }) {
  return (
    <div style={{ background: "#fff", border: `1.5px solid ${C.border}`, borderRadius: 14, padding: "20px 22px", marginBottom: 16 }}>
      <div style={{ fontSize: 15, fontWeight: 800, color: C.purple, marginBottom: 16 }}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <Label required={required}>{label}</Label>
      {children}
      {error && <div style={{ color: "#e53935", fontSize: 12, marginTop: 4 }}>{error}</div>}
    </div>
  );
}

const emptyGuardian = { lastName: "", firstName: "", relation: "", phone: "", email: "" };

export default function ElementaryPage() {
  const [child, setChild] = useState({ lastName: "", firstName: "", regno: "", birthDate: "", gender: "", address: "" });
  const [guardians, setGuardians] = useState([{ ...emptyGuardian }, { ...emptyGuardian }]);
  const [sections, setSections] = useState([]);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [rdAuto, setRdAuto] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    instance.get("elementary/form").then((res) => setSections(res.data || [])).catch(() => {});
  }, []);

  const setC = (k) => (e) => {
    const v = e.target.value;
    setChild((f) => {
      const next = { ...f, [k]: v };
      if (k === "regno") {
        const rd = parseRD(v);
        if (rd) {
          next.birthDate = rd.birthDate;
          next.gender = rd.gender;
          setRdAuto(true);
        } else setRdAuto(false);
      }
      return next;
    });
  };
  const setG = (i, k) => (e) => {
    const v = e.target.value;
    setGuardians((g) => g.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  };

  const validate = () => {
    const er = {};
    if (!child.lastName.trim()) er.cLastName = "Овог оруулна уу";
    if (!child.firstName.trim()) er.cFirstName = "Нэр оруулна уу";
    if (!parseRD(child.regno)) er.cRegno = "Регистрийн дугаар буруу байна (ж: АБ19291234)";
    if (!child.birthDate) er.cBirthDate = "Төрсөн огноо оруулна уу";
    if (!child.gender) er.cGender = "Хүйс сонгоно уу";
    if (!child.address.trim()) er.cAddress = "Хаяг оруулна уу";
    guardians.forEach((g, i) => {
      if (!g.lastName.trim()) er[`g${i}LastName`] = "Овог оруулна уу";
      if (!g.firstName.trim()) er[`g${i}FirstName`] = "Нэр оруулна уу";
      if (!g.relation.trim()) er[`g${i}Relation`] = "Хамаарлаа бичнэ үү (ээж, аав г.м)";
      if (!PHONE_RE.test(g.phone.trim())) er[`g${i}Phone`] = "Утас 8 оронтой байх ёстой";
      if (!EMAIL_RE.test(g.email.trim())) er[`g${i}Email`] = "Имэйл буруу форматтай байна";
    });
    sections.forEach((s) =>
      s.questions.forEach((q) => {
        if (q.required && !String(answers[q.id] || "").trim()) er[`q${q.id}`] = "Хариулт бөглөнө үү";
      })
    );
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async () => {
    if (!validate()) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setSubmitting(true);
    try {
      await instance.post("elementary/register", {
        child,
        guardians,
        answers: Object.entries(answers).map(([questionId, answer]) => ({ questionId: Number(questionId), answer })),
      });
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (e) {
      const msg = e?.response?.data?.error;
      if (msg) setErrors({ submit: msg });
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div style={{ maxWidth: 620, margin: "0 auto", padding: "60px 16px", textAlign: "center", fontFamily: "'Raleway','Segoe UI',sans-serif" }}>
        <img src={logo} alt="Томүжин" style={{ width: 190, objectFit: "contain", margin: "0 auto 18px", display: "block" }} />
        <div style={{ fontSize: 56 }}>🎉</div>
        <h2 style={{ color: C.purple, margin: "12px 0" }}>Өргөдөл амжилттай илгээгдлээ</h2>
        <p style={{ color: "#555", fontSize: 15, lineHeight: 1.6 }}>
          Таны 1-р ангийн элсэлтийн өргөдлийг хүлээн авлаа. Бид таны бүртгүүлсэн
          имэйл, утсаар холбогдож дараагийн алхмын мэдээллийг өгөх болно.
        </p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 680, margin: "0 auto", padding: "28px 16px 60px", fontFamily: "'Raleway','Segoe UI',sans-serif" }}>
      <div style={{ textAlign: "center", marginBottom: 22 }}>
        <img src={logo} alt="Томүжин" style={{ width: 190, objectFit: "contain", margin: "0 auto 14px", display: "block" }} />
        <h1 style={{ color: C.purple, fontSize: 24, marginBottom: 6 }}>1-р ангийн элсэлтийн өргөдөл</h1>
        <div style={{ color: "#777", fontSize: 14 }}>Томүжин Алтернатив сургууль · 2026-2027 хичээлийн жил</div>
      </div>

      {errors.submit && (
        <div style={{ background: "#fdecea", border: "1.5px solid #e53935", color: "#b71c1c", borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 14 }}>
          {errors.submit}
        </div>
      )}

      <Card title="Хүүхдийн мэдээлэл">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 14px" }}>
          <Field label="Овог" required error={errors.cLastName}>
            <input style={inputStyle(errors.cLastName)} value={child.lastName} onChange={setC("lastName")} />
          </Field>
          <Field label="Нэр" required error={errors.cFirstName}>
            <input style={inputStyle(errors.cFirstName)} value={child.firstName} onChange={setC("firstName")} />
          </Field>
        </div>
        <Field label="Регистрийн дугаар" required error={errors.cRegno}>
          <input style={inputStyle(errors.cRegno)} placeholder="АБ19291234" value={child.regno} onChange={setC("regno")} />
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 14px" }}>
          <Field label={`Төрсөн огноо${rdAuto ? " (РД-аас ✓)" : ""}`} required error={errors.cBirthDate}>
            <input type="date" style={inputStyle(errors.cBirthDate)} value={child.birthDate} onChange={setC("birthDate")} />
          </Field>
          <Field label="Хүйс" required error={errors.cGender}>
            <div style={{ display: "flex", gap: 16, paddingTop: 10 }}>
              {["Эмэгтэй", "Эрэгтэй"].map((g) => (
                <label key={g} style={{ fontSize: 15, cursor: "pointer" }}>
                  <input type="radio" name="gender" checked={child.gender === g} onChange={() => setChild((f) => ({ ...f, gender: g }))} style={{ marginRight: 6 }} />
                  {g}
                </label>
              ))}
            </div>
          </Field>
        </div>
        <Field label="Оршин суугаа хаяг" required error={errors.cAddress}>
          <input style={inputStyle(errors.cAddress)} placeholder="Дүүрэг, хороо, хотхон..." value={child.address} onChange={setC("address")} />
        </Field>
      </Card>

      {[0, 1].map((i) => (
        <Card key={i} title={`Асран хамгаалагч - ${i + 1}`}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 14px" }}>
            <Field label="Овог" required error={errors[`g${i}LastName`]}>
              <input style={inputStyle(errors[`g${i}LastName`])} value={guardians[i].lastName} onChange={setG(i, "lastName")} />
            </Field>
            <Field label="Нэр" required error={errors[`g${i}FirstName`]}>
              <input style={inputStyle(errors[`g${i}FirstName`])} value={guardians[i].firstName} onChange={setG(i, "firstName")} />
            </Field>
          </div>
          <Field label="Хүүхдийн юу нь болох?" required error={errors[`g${i}Relation`]}>
            <input style={inputStyle(errors[`g${i}Relation`])} placeholder="Ээж, аав, эмээ..." value={guardians[i].relation} onChange={setG(i, "relation")} />
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 14px" }}>
            <Field label="Утасны дугаар" required error={errors[`g${i}Phone`]}>
              <input style={inputStyle(errors[`g${i}Phone`])} maxLength={8} value={guardians[i].phone} onChange={setG(i, "phone")} />
            </Field>
            <Field label="Имэйл хаяг" required error={errors[`g${i}Email`]}>
              <input style={inputStyle(errors[`g${i}Email`])} value={guardians[i].email} onChange={setG(i, "email")} />
            </Field>
          </div>
        </Card>
      ))}

      {sections.map((s) => (
        <Card key={s.id} title={s.title}>
          {s.questions.map((q) => (
            <Field key={q.id} label={q.question} required={!!q.required} error={errors[`q${q.id}`]}>
              {q.qType === "long_text" ? (
                <textarea
                  rows={4}
                  style={{ ...inputStyle(errors[`q${q.id}`]), resize: "vertical" }}
                  value={answers[q.id] || ""}
                  onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                />
              ) : (
                <input
                  style={inputStyle(errors[`q${q.id}`])}
                  value={answers[q.id] || ""}
                  onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                />
              )}
            </Field>
          ))}
        </Card>
      ))}

      <button
        onClick={submit}
        disabled={submitting}
        style={{
          width: "100%",
          padding: "14px",
          borderRadius: 12,
          border: "none",
          background: C.purple,
          color: "#fff",
          fontSize: 16,
          fontWeight: 800,
          cursor: submitting ? "wait" : "pointer",
          opacity: submitting ? 0.7 : 1,
          fontFamily: "inherit",
        }}
      >
        {submitting ? "Илгээж байна..." : "Өргөдөл илгээх"}
      </button>
    </div>
  );
}
